import { getDevIdentity, isDevLoginEnabled } from './devLogin';
import { useSession } from './useSession';

export type Role = 'Approver' | 'Originator' | 'Advisor';

export type RolesResult = {
  /** Single recognized role, or null if none / dual. */
  role: Role | null;
  /** True iff the user holds more than one recognized role. */
  hasDualRole: boolean;
  /** Display name from the session, if any. */
  name: string | null;
  /** User object id; never persisted. */
  oid: string | null;
};

/**
 * Resolves the current user's role from the server-side session (GET /api/v1/me).
 * In Dev Login mode (localhost + Dev only) returns the synthetic identity instead.
 */
export function useRoles(): RolesResult {
  // Dev Login (localhost + Dev only): synthetic identity from the picked user.
  if (isDevLoginEnabled()) {
    const identity = getDevIdentity();
    return {
      role: identity?.role ?? null,
      hasDualRole: false,
      name: identity?.name ?? null,
      oid: identity?.oid ?? null,
    };
  }

  // BFF session: roles come from the server-side session record.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { user } = useSession();
  if (!user) {
    return { role: null, hasDualRole: false, name: null, oid: null };
  }

  const hasDualRole = user.roles.length > 1;
  const role: Role | null = user.roles.length === 1 ? user.roles[0]! : null;

  return {
    role,
    hasDualRole,
    name: user.name,
    oid: user.oid,
  };
}
